import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useSearchParams, useParams } from "react-router-dom";
import { X, Loader2, Phone, Mail } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ChatHeader } from "../chat/ChatHeader";
import { ChatInput } from "../chat/ChatInput";
import { LucideIconByName, IconMap } from "@/components/ui/lucide-icons";
import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/types/database.types";
import { sanitizeBrandColor } from "@/lib/utils";

// Add to top of file, before any imports
declare global {
  interface Window {
    GLOBAL_PRE_SESSION_FIELDS?: Record<string, any>;
  }
}

// Define a type for our pre-session field
interface PreSessionField extends Tables<"form_fields"> {
  id: string;
  title: string;
  is_required: boolean;
}

// Create base schema with optional fields that become required based on settings
const baseSchema = {
  // Fields will be conditionally required based on agent settings
};

// We'll dynamically create the schema based on agent settings
const createFormSchema = (requireName = true, requireEmail = true, requirePhone = false, preSessionFields = []) => {
  const schema = {
    ...(requireName ? {
      name: z.string().min(2, "Name must be at least 2 characters"),
    } : {
      name: z.string().optional()
    }),
    ...(requireEmail ? {
      email: z.string().email("Please enter a valid email address"),
    } : {
      email: z.string().optional()
    }),
    ...(requirePhone ? {
      phone: z.string().min(7, "Please enter a valid phone number")
    } : {
      phone: z.string().optional()
    })
  };
  
  // Add dynamic pre-session fields to the schema
  preSessionFields.forEach(field => {
    // Create field key using a safe version of the field title
    const fieldKey = `pre_session_${field.id}`;
    
    // Add to schema with validation based on required status
    if (field.is_required) {
      schema[fieldKey] = z.string().min(1, `${field.title} is required`);
    } else {
      schema[fieldKey] = z.string().optional();
    }
  });
  
  return z.object(schema);
};

interface PublicAgentAuthFormProps {
  onSubmit: (data: any) => Promise<void>;
  agent?: any;
  organization?: any;
  isEmbedded?: boolean;
  onClose?: () => void;
}

const PublicAgentAuthForm: React.FC<PublicAgentAuthFormProps> = ({
  onSubmit,
  agent,
  organization,
  isEmbedded = false,
  onClose
}) => {
  const [searchParams] = useSearchParams();
  const isEmbeddedParam = searchParams.get('embed') === 'true';
  const hideHeaderParam = searchParams.get('hideHeader') === 'true';
  const hideNavBar = searchParams.get('hideNavBar') === 'true';
  const actuallyEmbedded = isEmbedded || isEmbeddedParam;
  const shouldHideHeader = hideHeaderParam || (isEmbedded || isEmbeddedParam); // Hide header if embedded or explicitly requested
  const isInBubbleWidget = searchParams.get('embedBubble') === 'true';

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [preSessionFields, setPreSessionFields] = useState([]);
  const [isLoadingFields, setIsLoadingFields] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  
  // Check field requirements based on agent settings
  const contactInfo = agent?.contact_info || {};
  const requireName = contactInfo.requireName !== false; // Default to true if not set
  const requireEmail = contactInfo.requireEmail !== false; // Default to true if not set
  const requirePhoneNumber = contactInfo.requirePhoneNumber === true; // Default to false if not set
  
  // Get publicId from URL if possible 
  const { publicId } = useParams<{ publicId: string }>();

  // Ensure we have a global space to store pre_session_fields
  if (typeof window !== 'undefined') {
    window.GLOBAL_PRE_SESSION_FIELDS = window.GLOBAL_PRE_SESSION_FIELDS || {};
  }
  
  // Fetch pre-session fields when component mounts
  useEffect(() => {
    const fetchPreSessionFields = async () => {
      if (!agent?.id) {
        console.log("Can't fetch pre-session fields - missing agent.id");
        return;
      }
      
      setIsLoadingFields(true);
      try {
        // Get pre-session fields for this agent
        const { data, error } = await supabase
          .from("form_fields")
          .select("*")
          .eq("agent_id", agent.id)
          .eq("field_type", "pre-session")
          .order("position", { ascending: true });
        
        if (!error && data && data.length > 0) {
          setPreSessionFields(data);
        } else {
          // Try alternative field type name
          const { data: altData, error: altError } = await supabase
            .from("form_fields")
            .select("*")
            .eq("agent_id", agent.id)
            .eq("field_type", "presession")
            .order("position", { ascending: true });
          
          if (!altError && altData && altData.length > 0) {
            setPreSessionFields(altData);
          }
        }
      } catch (error) {
        console.error("Error fetching pre-session fields:", error);
      } finally {
        setIsLoadingFields(false);
      }
    };
    
    fetchPreSessionFields();
  }, [agent?.id]);
  
  // Create the appropriate form schema based on field requirements - memoized to prevent recreation on every render
  const formSchema = useMemo(
    () => createFormSchema(requireName, requireEmail, requirePhoneNumber, preSessionFields),
    [requireName, requireEmail, requirePhoneNumber, preSessionFields]
  );
  
  // Prepare default values for all fields including dynamic pre-session fields - memoized
  const getDefaultValues = useCallback(() => {
    const defaults = {
      name: "",
      email: "",
      phone: ""
    };
    
    // Add pre-session fields with empty default values
    preSessionFields.forEach(field => {
      defaults[`pre_session_${field.id}`] = "";
    });
    
    return defaults;
  }, [preSessionFields]);
  
  // Set up form with dynamic schema - recreate when schema changes to ensure resolver updates
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: getDefaultValues(),
  });

  // Reset form when schema or default values change
  useEffect(() => {
    form.reset(getDefaultValues());
  }, [form, getDefaultValues]);

  const handleSubmit = async (data: z.infer<typeof formSchema>) => {
    try {
      setIsSubmitting(true);
      
      // Extract pre-session fields data into a separate object
      const preSessionData = {};
      
      preSessionFields.forEach(field => {
        const fieldKey = `pre_session_${field.id}`;
        if (data[fieldKey]) {
          // Use the field.id as the key to maintain consistency
          preSessionData[field.id] = data[fieldKey];
          // Remove from main data object to avoid duplication
          delete data[fieldKey];
        }
      });
      
      // Add a timestamp for debugging purposes
      preSessionData["_timestamp"] = new Date().toISOString();
      
      // Create a clean copy of the form data
      const cleanData = { ...data };
      
      // Add pre-session data to the submission
      const enrichedData = {
        ...cleanData,
        pre_session_fields: preSessionData
      };
      
      // Store in multiple places to ensure data persists
      try {
        // Store in global variable for immediate access
        window.GLOBAL_PRE_SESSION_FIELDS = preSessionData;
        
        // Store in sessionStorage (survives page reloads within same tab)
        sessionStorage.setItem('GLOBAL_PRE_SESSION_FIELDS', JSON.stringify(preSessionData));
        
        // Store directly in localStorage as fallback
        localStorage.setItem('DIRECT_PRE_SESSION_FIELDS', JSON.stringify(preSessionData));
      } catch (e) {
        console.error("Error saving backup pre-session fields:", e);
      }
      
      // Continue with normal form submission
      await onSubmit(enrichedData);

      // If embedded, also send a message to the parent window
      if (actuallyEmbedded && window.parent !== window) {
        window.parent.postMessage({
          type: 'auth_complete',
          agentId: agent?.public_id,
          userData: enrichedData
        }, '*');
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get branding elements with sanitization
  const rawBrandColor = organization?.brand_color || agent?.organization?.brand_color || "#060520";
  const brandColor = sanitizeBrandColor(rawBrandColor);
  const bannerUrl = organization?.banner_url || agent?.organization?.banner_url;

  // Get organization name for display or fallback to agent name
  const displayName = organization?.name || agent?.title || "AI Assistant";
  
  // Header avatar (like ChatHeader): organization logo only (no fallback to agent avatar)
  const headerAvatarUrl = organization?.logo_url;
  
  // Chat bubble avatar: agent avatar (this represents who is sending the message)
  const chatBubbleAvatarUrl = agent?.avatar_url;

  // Create a proper transparent version of the brand color - fix hex opacity from 15 to 26 for 15%
  const getTransparentBrandColor = (color: string) => {
    // Remove # if present
    const cleanColor = color.replace('#', '');
    
    // Check if it's a 3-digit hex code (like #fff or #abc)
    if (cleanColor.length === 3) {
      // Convert 3-digit to 6-digit hex
      const expandedColor = cleanColor.split('').map(char => char + char).join('');
      return `#${expandedColor}26`; // 26 = 15% opacity in hex
    }
    
    // Check if it's a 6-digit hex code
    if (cleanColor.length === 6) {
      return `#${cleanColor}26`; // 26 = 15% opacity in hex
    }
    
    // For other formats (rgb, rgba, named colors), use CSS rgba
    return `rgba(0, 0, 0, 0.15)`; // Fallback to black with opacity
  };
  
  const transparentBrandColor = getTransparentBrandColor(brandColor);

  // Add resize listener to detect small screens
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 380);
    };
    
    // Set initial value
    handleResize();
    
    // Add listener
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Create a modified version of the ChatHeader component that doesn't show the tagline
  const CustomChatHeader = () => {
    // Header text: always organization name
    const displayName = organization?.name || "Assistant";
    // Header image: organization logo only (no fallback to agent avatar)
    const headerAvatarUrl = organization?.logo_url;

    // Agent specific setting for phone number display
    const agentContactInfo = agent?.contact_info as { requirePhoneNumber?: boolean } | undefined;
    const shouldShowPhoneNumber = agentContactInfo?.requirePhoneNumber !== false;

    return (
      <div className="flex flex-col">
        <div
          className="text-white px-5 py-4 flex items-center justify-between"
          style={{ backgroundColor: brandColor }}
        >
          <div className="flex items-center gap-4">
            {headerAvatarUrl ? (
              typeof headerAvatarUrl === 'string' && IconMap[headerAvatarUrl] ? (
                // If avatar_url is a Lucide icon name
                <div className="h-10 w-10 rounded-full bg-white p-1 flex items-center justify-center">
                  <LucideIconByName name={headerAvatarUrl} size={20} className="text-gray-700" />
                </div>
              ) : (
                // If avatar_url is an image URL
                <img
                  src={headerAvatarUrl}
                  alt={displayName}
                  className="h-10 w-10 object-contain rounded-full bg-white p-1"
                />
              )
            ) : null}
            <div>
              <h2 className="font-semibold text-sm text-white">{displayName}</h2>
              {/* Tagline removed */}
            </div>
          </div>

          {/* Contact buttons */}
          <div className="flex items-center gap-2">
            {organization?.contact_info && (
              <>
                {shouldShowPhoneNumber && (organization.contact_info as any)?.phone && (
                  <a
                    href={`tel:${(organization.contact_info as any).phone}`}
                    className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
                    aria-label="Call us"
                  >
                    <Phone size={16} />
                  </a>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Always apply the same background system as public chat page (2-layer system) - fix brand color usage
  const containerStyle = {
    background: `linear-gradient(to bottom, ${sanitizeBrandColor(organization?.brand_color || '#3b82f6')}26 0%, #ffffff 100%)`,
    minHeight: (actuallyEmbedded || isInBubbleWidget) ? '100%' : '100dvh',
    height: (actuallyEmbedded || isInBubbleWidget) ? '100%' : 'auto',
  };

  const hasCustomBackground = organization?.chat_background &&
                              organization.chat_background !== 'default' &&
                              organization.chat_background !== 'Default - Linear Background';

  const customBackgroundUrl = hasCustomBackground
    ? supabase.storage.from('image_library').getPublicUrl(`chat_backgrounds/${organization.chat_background}`).data.publicUrl
    : null;

  useEffect(() => {
    document.title = `Chat with ${agent?.title} from ${organization?.name}`;
  }, [agent?.title, organization?.name]);

  return (
    <div 
      className={`relative flex ${actuallyEmbedded ? 'h-full min-h-0' : 'min-h-[100dvh]'} flex-col`}
      style={containerStyle}
    >
      {/* Custom Background Overlay */}
      {hasCustomBackground && customBackgroundUrl && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: organization?.brand_color || '#3b82f6',
            maskImage: `url(${customBackgroundUrl})`,
            WebkitMaskImage: `url(${customBackgroundUrl})`,
            maskSize: 'cover',
            WebkitMaskSize: 'cover',
            maskPosition: 'center center',
            WebkitMaskPosition: 'center center',
            maskRepeat: 'no-repeat',
            WebkitMaskRepeat: 'no-repeat',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            opacity: 0.06,
            zIndex: 0,
            pointerEvents: 'none',
          }}
        />
      )}
      
      {/* Chat-like header (only when not embedded) */}
      {!actuallyEmbedded && (
        <div
          className="flex items-center gap-3 px-5 py-3"
          style={{ backgroundColor: brandColor }}
        >
          {headerAvatarUrl ? (
            typeof headerAvatarUrl === 'string' && IconMap[headerAvatarUrl] ? (
              <div className="h-10 w-10 rounded-full bg-white flex items-center justify-center">
                <LucideIconByName name={headerAvatarUrl} size={20} className="text-gray-700" />
              </div>
            ) : (
              <img
                src={headerAvatarUrl}
                alt={displayName}
                className="h-10 w-10 object-contain rounded-full bg-white p-1"
              />
            )
          ) : null}
          <h2 className="text-white text-sm font-semibold truncate">
            {displayName}
          </h2>
        </div>
      )}

      {/* Chat-like content area with max width like main chat view */}
      <div className="flex-1 flex flex-col relative z-10">
        <div className="max-w-[770px] mx-auto w-full p-5">
          {/* Organization Banner - show if organization has a banner image */}
          {organization?.banner_url && (
            <div className="w-full mb-5">
              <img
                src={organization.banner_url}
                alt="Organization Banner"
                className="w-full rounded-[7px] h-auto object-cover max-h-[140px]"
              />
            </div>
          )}

          {/* Agent message bubble containing the form */}
          <div className="flex items-start gap-2 mb-6">
            {/* Agent avatar with green online dot */}
            <div className="relative flex-shrink-0">
              <Avatar className="h-[50px] w-[50px]">
                {chatBubbleAvatarUrl ? (
                  typeof chatBubbleAvatarUrl === 'string' && IconMap[chatBubbleAvatarUrl] ? (
                    <div className="h-full w-full rounded-full bg-white flex items-center justify-center">
                      <LucideIconByName name={chatBubbleAvatarUrl} size={20} className="text-gray-700" />
                    </div>
                  ) : (
                    <AvatarImage src={chatBubbleAvatarUrl} alt={agent?.title || displayName} className="object-contain" />
                  )
                ) : null}
                <AvatarFallback style={{ backgroundColor: brandColor, color: "#ffffff" }}>
                  {(agent?.title || displayName).substring(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              {/* Green online status dot */}
              <div 
                className="absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white"
                style={{ backgroundColor: '#059669' }}
              />
            </div>

            {/* Chat bubble containing form - completely opaque white background */}
            <div 
              className="p-4 max-w-[80%] text-black rounded-2xl flex-1 relative bg-white"
              style={{
                backgroundColor: '#ffffff !important',
                borderRadius: "14px",
                boxShadow: "1px 1px 3px rgba(0, 0, 0, 0.08)",
                border: 'none',
                zIndex: 20,
                position: 'relative'
              }}
            >
              {/* Welcome message */}
              <div className="mb-4">
                <h1 className="text-lg font-semibold mb-2" style={{ color: brandColor }}>
                  {agent?.splash_page_title || "Hi there!"}
                </h1>
                <p className="text-sm text-gray-700">
                  {agent?.splash_page_subtitle || "We just need a few details from you before we start our conversation."}
                </p>
              </div>

              {/* Form */}
              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-3">
                  {/* Only show name field if required */}
                  {requireName && (
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-700 font-medium text-sm">
                            Name {requireName && <span className="text-red-500">*</span>}
                          </FormLabel>
                          <FormControl>
                            <Input placeholder="Enter your name" {...field} className="text-black bg-white text-sm" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  {/* Only show email field if required */}
                  {requireEmail && (
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-700 font-medium text-sm">
                            Email {requireEmail && <span className="text-red-500">*</span>}
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder="Enter your email"
                              {...field}
                              className="text-black bg-white text-sm"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                  
                  {/* Phone number field - only shown if requirePhoneNumber is true */}
                  {requirePhoneNumber && (
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-700 font-medium text-sm">
                            Phone Number {requirePhoneNumber && <span className="text-red-500">*</span>}
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="tel"
                              placeholder="Enter your phone number"
                              {...field}
                              className="text-black bg-white text-sm"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                  
                  {/* Pre-session fields - dynamically generated based on fetched fields */}
                  {preSessionFields.length > 0 && (
                    <div className="space-y-3">
                      {preSessionFields.map((field: any) => {
                        const answerType = field.answer_type || 'text';
                        const fieldKey = `pre_session_${field.id}`;
                        return (
                          <FormField
                            key={field.id}
                            control={form.control}
                            name={fieldKey as any}
                            render={({ field: formField }) => (
                              <FormItem>
                                <FormLabel className="text-gray-700 font-medium text-sm">
                                  {field.title}
                                  {field.is_required && <span className="text-red-500 ml-1">*</span>}
                                </FormLabel>
                                <FormControl>
                                  {answerType === 'select' && Array.isArray(field.options) ? (
                                    <select
                                      {...formField}
                                      className="text-black bg-white border rounded px-3 py-2 w-full text-sm"
                                    >
                                      <option value="">Select {field.title.toLowerCase()}</option>
                                      {field.options.map((opt: string) => (
                                        <option key={opt} value={opt}>{opt}</option>
                                      ))}
                                    </select>
                                  ) : answerType === 'textarea' ? (
                                    <textarea
                                      {...formField}
                                      rows={3}
                                      className="text-black bg-white border rounded px-3 py-2 w-full text-sm"
                                      placeholder={`Enter ${field.title.toLowerCase()}`}
                                    />
                                  ) : answerType === 'date' ? (
                                    <Input
                                      type="date"
                                      {...formField}
                                      className="text-black bg-white text-sm"
                                    />
                                  ) : answerType === 'number' ? (
                                    <Input
                                      type="number"
                                      {...formField}
                                      className="text-black bg-white text-sm"
                                    />
                                  ) : answerType === 'email' ? (
                                    <Input
                                      type="email"
                                      {...formField}
                                      className="text-black bg-white text-sm"
                                      placeholder={`Enter ${field.title.toLowerCase()}`}
                                    />
                                  ) : answerType === 'phone' ? (
                                    <Input
                                      type="tel"
                                      {...formField}
                                      className="text-black bg-white text-sm"
                                      placeholder={`Enter ${field.title.toLowerCase()}`}
                                    />
                                  ) : (
                                    <Input
                                      placeholder={`Enter ${field.title.toLowerCase()}`}
                                      {...formField}
                                      className="text-black bg-white text-sm"
                                    />
                                  )}
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        );
                      })}
                    </div>
                  )}

                  {/* Start button inside the message bubble */}
                  <div className="mt-4 pt-2">
                    <Button
                      onClick={form.handleSubmit(handleSubmit)}
                      disabled={isSubmitting || isLoadingFields}
                      className="w-full rounded-lg py-3 flex items-center justify-center gap-2"
                      style={{
                        backgroundColor: brandColor,
                        color: brandColor === "#ffffff" ? "#000000" : "#ffffff"
                      }}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Please wait...
                        </>
                      ) : isLoadingFields ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        agent?.start_button_text || 'Start Chat'
                      )}
                    </Button>
                    
                    {/* Legal disclaimer - moved below the start button */}
                    {organization?.legal_disclaimer && (
                      <div className="text-xs text-gray-500 mt-3 text-center" style={{ fontSize: "11px" }}>
                        {organization.legal_disclaimer}
                      </div>
                    )}
                  </div>
                </form>
              </Form>
            </div>
          </div>
        </div>
      </div>

      {/* Floating ChatInput component (disabled) at bottom - same position as real chat */}
      {/* 
      <div className="absolute bottom-0 left-0 right-0 bg-transparent p-4 z-30">
        <div className="max-w-[770px] mx-auto">
          <ChatInput
            onSendMessage={() => {}} // No-op function
            isLoading={false}
            brandColor={brandColor}
            isPublic={true}
            isMobile={false}
            isDisabled={true}
            placeholder="Complete the form above to start chatting..."
          />
        </div>
      </div>
      */}
    </div>
  );
};

export default PublicAgentAuthForm;
